import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { simulacionesGpsService } from '../services/simulacionesGpsService'
import { toast } from 'react-hot-toast'
import { 
  ArrowLeft, 
  Send, 
  MapPin,
  Smartphone,
  Navigation,
  Zap,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Globe,
  Target,
  Info
} from 'lucide-react'

const SimularPosicionGPS = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [datos, setDatos] = useState({
    imei: '',
    sistema: 'https://gps.tecnytte.com',
    lat: '',
    lng: '',
    altitude: 100,
    angle: 45,
    speed: 0,
    loc_valid: '1',
    ignition: '1',
    sos: '0',
    event: 'normal'
  })

  const sistemasGPS = [
    { value: 'https://gps.tecnytte.com', label: 'GPS Tecnytte' },
    { value: 'https://gps-plus.com', label: 'GPS Plus' },
    { value: 'https://zz-factory.com', label: 'ZZ Factory' },
    { value: 'https://gps2.tecnytte.com', label: 'GPS2 Tecnytte' }
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setLoading(true)

      // Validaciones
      if (!datos.imei.trim()) {
        toast.error('El IMEI es requerido')
        return
      }

      if (!datos.lat || !datos.lng) {
        toast.error('La latitud y longitud son requeridas')
        return
      }

      if (!datos.sistema) {
        toast.error('Debe seleccionar un sistema GPS')
        return
      }

      await simulacionesGpsService.simularPosicion(datos)
      toast.success('Posición simulada exitosamente')
      
      // Limpiar formulario después del envío exitoso
      setDatos(prev => ({
        ...prev,
        lat: '',
        lng: '',
        altitude: 100,
        angle: 45,
        speed: 0,
        sos: '0',
        event: 'normal'
      }))
    } catch (error) {
      console.error('Error simulando posición:', error)
      toast.error('Error al simular la posición')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setDatos(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const obtenerPosicionActual = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setDatos(prev => ({
            ...prev,
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }))
          toast.success('Posición actual obtenida')
        },
        (error) => {
          console.error('Error obteniendo posición:', error)
          toast.error('No se pudo obtener la posición actual')
        }
      )
    } else {
      toast.error('Geolocalización no soportada en este navegador')
    }
  }

  const establecerPosicionEjemplo = () => {
    setDatos(prev => ({
      ...prev,
      lat: -12.345678,
      lng: -78.901234
    }))
    toast.success('Posición de ejemplo establecida')
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Simular Posición GPS</h1>
          <p className="text-gray-600">Envía posiciones GPS a sistemas externos</p>
        </div>
        <button
          onClick={() => navigate('/simulaciones-gps')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulario */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Datos de Posición
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Sistema GPS */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Globe className="w-4 h-4 inline mr-1" />
                Sistema GPS
              </label>
              <select
                name="sistema"
                value={datos.sistema}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {sistemasGPS.map(sistema => (
                  <option key={sistema.value} value={sistema.value}>
                    {sistema.label}
                  </option>
                ))}
              </select>
            </div>

            {/* IMEI */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Smartphone className="w-4 h-4 inline mr-1" />
                IMEI
              </label>
              <input
                type="text"
                name="imei"
                value={datos.imei}
                onChange={handleChange}
                placeholder="123456789012345"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Coordenadas */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Latitud
                </label>
                <input
                  type="number"
                  step="any"
                  name="lat"
                  value={datos.lat}
                  onChange={handleChange}
                  placeholder="-12.345678"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Longitud
                </label>
                <input
                  type="number"
                  step="any"
                  name="lng"
                  value={datos.lng}
                  onChange={handleChange}
                  placeholder="-78.901234"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            {/* Botones de posición */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={obtenerPosicionActual}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
              >
                <Navigation className="w-4 h-4" />
                Posición Actual
              </button>
              <button
                type="button"
                onClick={establecerPosicionEjemplo}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
              >
                <MapPin className="w-4 h-4" />
                Ejemplo
              </button>
            </div>

            {/* Parámetros adicionales */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Altitud
                </label>
                <input
                  type="number"
                  name="altitude"
                  value={datos.altitude}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ángulo
                </label>
                <input
                  type="number"
                  name="angle"
                  value={datos.angle}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Velocidad
                </label>
                <input
                  type="number"
                  name="speed"
                  value={datos.speed}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Localización
                </label>
                <select
                  name="loc_valid"
                  value={datos.loc_valid}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1">Válida</option>
                  <option value="0">Inválida</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ignición
                </label>
                <select
                  name="ignition"
                  value={datos.ignition}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1">Encendido</option>
                  <option value="0">Apagado</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado SOS
                </label>
                <select
                  name="sos"
                  value={datos.sos}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="0">Normal</option>
                  <option value="1">SOS</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Evento
              </label>
              <input
                type="text"
                name="event"
                value={datos.event}
                onChange={handleChange}
                placeholder="normal"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Botón de envío */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg flex items-center justify-center gap-2 font-medium"
            >
              <Send className="w-5 h-5" />
              {loading ? 'Enviando...' : 'Enviar Posición'}
            </button>
          </form>
        </div>

        {/* Panel de información */}
        <div className="bg-white rounded-lg shadow p-6">
                      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Información
            </h2>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Instrucciones
              </h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Selecciona el sistema GPS destino</li>
                <li>• Ingresa el IMEI del dispositivo</li>
                <li>• Establece las coordenadas (lat/lng)</li>
                <li>• Configura los parámetros adicionales</li>
                <li>• Haz clic en "Enviar Posición"</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-medium text-yellow-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Notas Importantes
              </h3>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>• La posición se enviará al sistema seleccionado</li>
                <li>• Verifica que el IMEI sea correcto</li>
                <li>• Las coordenadas deben ser válidas</li>
                <li>• El sistema debe estar disponible</li>
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Parámetros
              </h3>
              <div className="text-sm text-gray-700 space-y-1">
                <p><strong>Altitud:</strong> Altura sobre el nivel del mar</p>
                <p><strong>Ángulo:</strong> Dirección del movimiento (grados)</p>
                <p><strong>Velocidad:</strong> Velocidad del vehículo (km/h)</p>
                <p><strong>Ignición:</strong> Estado del motor</p>
                <p><strong>SOS:</strong> Estado de emergencia</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SimularPosicionGPS 